import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { useSEO } from "@/lib/useSEO";
import { api } from "@/lib/api";
import { ArrowRight, Trophy, Users, Heart, Zap } from "lucide-react";
import { PHOTO_COACH_PORTRAIT, PHOTO_RING_PAD_WORK } from "@/lib/brand";

const BRING_ICONS = [Trophy, Heart, Zap, Users];

export default function About() {
  useSEO({
    title: "About Coach Chris — Online Boxing Coach for Beginners & Beyond | Better Through Boxing",
    description: "Meet Coach Chris Persaud — founder of The Flow State Method LLC and creator of the Better Through Boxing program. An online boxing coach who's spent 7+ years making boxing accessible. Patient, deeply technical, and great with beginners — over Zoom or in person in New Jersey.",
    path: "/about",
    keywords: ["online boxing coach", "boxing coach for beginners", "Coach Chris Persaud", "Better Through Boxing", "private boxing coach", "boxing instructor New Jersey", "virtual boxing lessons"],
    ogImage: PHOTO_COACH_PORTRAIT,
  });

  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/page-config/about").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  const c = cfg || {};
  const hero = c.hero || {}, story = c.story || {}, bring = c.bring || {}, quote = c.quote || {}, cta = c.cta || {};
  const storyParas = story.paragraphs || [
    "Most online boxing instruction is recorded video on a loop — pre-made workouts that can't see what you're actually doing. That works for fitness. It doesn't work for technique.",
    "Better Through Boxing was built around one idea: live coaching translates over Zoom if the coach knows how to teach it. Every session is private, real-time, and built around what you specifically need to work on. I see your stance, I correct your footwork, I push you when you need it, and I slow down when the technique deserves it.",
    "I'm especially proud of how well this works for beginners. Walking into a boxing gym is intimidating. Showing up on Zoom from your living room is not. You get the same coaching quality without the gym anxiety — and you build the foundation that makes the gym feel possible later, if you want it.",
  ];
  const bringCards = bring.cards || [
    { title: "7+ years of coaching", body: "From the corner of competitive amateurs to the patient first-day-ever beginner. Same eye for detail, different pace." },
    { title: "Patience built for beginners", body: "Boxing is hard. I make it feel less hard. No yelling, no ego, no pressure — just clear technique and real corrections." },
    { title: "Deeply technical breakdown", body: "I don't just demo a punch. I explain the chain — feet, hips, shoulder, fist — so you feel where the power comes from." },
    { title: "Real human accountability", body: "When you book, I personally text/call to schedule. When you train, it's me on the other end of the Zoom. No assistants, no rotating staff." },
  ];

  return (
    <div className="App relative">
      <SiteHeader />
      <main data-testid="about-page">
        {hero.enabled !== false && (
        <section className="border-b border-[#27272A]">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-4">{hero.kicker || "About · Coach Chris Persaud"}</div>
              <h1 className="font-display font-bold uppercase text-5xl md:text-7xl tracking-tight leading-[0.9] mb-6">
                {hero.title_pre || "A boxing coach who actually "}<span className="text-[#FF331F]">{hero.title_accent || "explains."}</span>
              </h1>
              <p className="text-zinc-300 leading-relaxed max-w-xl text-base md:text-lg whitespace-pre-line">
                {hero.body || "I'm Chris Persaud — founder of The Flow State Method LLC and creator of the Better Through Boxing program. I've spent the last 7+ years making boxing accessible: working with absolute beginners trying it for the first time, returning athletes shaking the rust off, and active competitors prepping for fights."}
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="theme-on-dark relative aspect-[4/5] overflow-hidden border border-[#27272A]">
                <img src={hero.image || PHOTO_COACH_PORTRAIT} alt="Coach Chris Persaud, online boxing coach and creator of the Better Through Boxing program" className="w-full h-full object-cover" />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#050505]/95 to-transparent p-5">
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-1">Coach</div>
                  <div className="font-display uppercase text-2xl">Chris Persaud</div>
                </div>
              </div>
            </div>
          </div>
        </section>
        )}

        {story.enabled !== false && (
        <section className="max-w-3xl mx-auto px-6 md:px-10 py-20">
          <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">{story.kicker || "The Method"}</div>
          <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-none mb-8">{story.heading || "Why Better Through Boxing."}</h2>
          <div className="prose-content space-y-5 text-zinc-200 text-[17px] leading-[1.7]">
            {storyParas.map((p, i) => <p key={i} className="whitespace-pre-line">{p}</p>)}
          </div>
        </section>
        )}

        {bring.enabled !== false && (
        <section className="bg-[#0F0F0F] border-y border-[#27272A]">
          <div className="max-w-6xl mx-auto px-6 md:px-10 py-20">
            <h2 className="font-display uppercase text-3xl md:text-5xl tracking-tight leading-none mb-12">{bring.heading || "What I bring to a session."}</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {bringCards.map((item, i) => {
                const Icon = BRING_ICONS[i % BRING_ICONS.length];
                return (
                  <div key={i} className="border border-[#27272A] bg-[#050505] p-7">
                    <Icon className="h-6 w-6 text-[#FF331F] mb-4" />
                    <h3 className="font-display uppercase text-xl mb-2 leading-tight">{item.title}</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed">{item.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        )}

        {quote.enabled !== false && (
        <section className="theme-on-dark relative">
          <img src={PHOTO_RING_PAD_WORK} alt="Coach Chris running pad work in a boxing ring" className="w-full h-[420px] object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12 text-center">
            <p className="text-zinc-300 italic max-w-2xl mx-auto leading-relaxed">
              &ldquo;{quote.text || "Chris doesn't just show you how to do something — he explains the movement in such detail that you can feel it."}&rdquo;
            </p>
            <Link to="/testimonials" className="inline-block mt-4 text-[#FF331F] text-xs uppercase tracking-widest font-bold hover:underline">More from athletes →</Link>
          </div>
        </section>
        )}

        {cta.enabled !== false && (
        <section className="max-w-4xl mx-auto px-6 md:px-10 py-24 text-center">
          <h2 className="font-display uppercase text-4xl md:text-6xl tracking-tight leading-[0.9] mb-6">{cta.heading || "Train with me."}</h2>
          <p className="text-zinc-300 max-w-2xl mx-auto mb-8">{cta.body || "Pick a Zoom session or start the 6-week Basics Program."} NJ residents can also <Link to="/in-person-training" className="text-[#FF331F] hover:underline">request in-person training</Link>.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/services"><Button variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900 hover:text-white font-bold uppercase tracking-widest rounded-sm bg-transparent h-12 px-6">See services</Button></Link>
            <Link to="/book"><Button className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm h-12 px-6" data-testid="about-cta-book">Book a session <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
          </div>
        </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
